/**
 * 403 `feature.disabled` — fonctionnalité fermée pour le locataire (UPT-2056).
 *
 * Quand une fonctionnalité du catalogue serveur est désactivée (`tenantFeatures.goals = false`), toute son API
 * répond 403 avec `{ "code": "feature.disabled", "feature": "goals" }`. C'est un ÉTAT, pas une panne : le
 * client le traduit en `FeatureDisabledError`, que les écrans montrent comme « fonctionnalité désactivée »
 * (FeatureDisabledState) au lieu d'un message d'erreur générique ou d'un toast « accès refusé ».
 *
 * Reconnu dans les deux formes de rejet axios du projet :
 *  - `error.response` (axios nu, shell React Router) ;
 *  - `{ error, status }` (intercepteur du shell legacy, app/legacy/src/index.tsx), où la réponse est dans `error.error.response`.
 * Un 403 sans ce code (droit manquant) n'est PAS concerné et garde son traitement habituel.
 */

export const FEATURE_DISABLED_CODE = 'feature.disabled';

/** Fonctionnalité inconnue du corps de la réponse (clé absente) : l'appelant décide d'un repli. */
const UNKNOWN_FEATURE = 'unknown';

export class FeatureDisabledError extends Error {
  readonly code = FEATURE_DISABLED_CODE;
  readonly feature: string;

  constructor(feature: string) {
    super(`Feature "${feature}" is disabled for this tenant`);
    this.name = 'FeatureDisabledError';
    this.feature = feature;
  }
}

type ResponseLike = { status?: unknown; data?: unknown };

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function responseOf(error: unknown): ResponseLike | null {
  if (!isObject(error)) return null;
  if (isObject(error.response)) return error.response as ResponseLike;
  if (isObject(error.error) && isObject(error.error.response)) return error.error.response as ResponseLike;
  return null;
}

function bodyOf(response: ResponseLike): Record<string, unknown> | null {
  let data = response.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data) as unknown;
    } catch {
      return null;
    }
  }
  return isObject(data) ? data : null;
}

/**
 * Corps JSON d'un rejet HTTP de statut `status` (axios nu ou `{ error, status }` du shell legacy), sinon null. Partagé
 * par les refus à code stable : 403 `feature.disabled`, 403 `module.inactive` (UPT-2069 L2), 409
 * `module.hasDependents` / `module.missingDependency` (UPT-2069 L3 : core/api/moduleLifecycleConflict.ts).
 */
export function rejectionBodyOf(error: unknown, status: number): Record<string, unknown> | null {
  const response = responseOf(error);
  if (response === null || response.status !== status) return null;
  return bodyOf(response);
}

/** Corps JSON d'un rejet 403, sinon null (voir {@link rejectionBodyOf}). */
export function forbiddenBodyOf(error: unknown): Record<string, unknown> | null {
  return rejectionBodyOf(error, 403);
}

/**
 * Traduit un rejet de requête en `FeatureDisabledError` si c'est un 403 `feature.disabled`, sinon null.
 * `fallbackFeature` : fonctionnalité de l'API appelée, utilisée si le corps ne nomme pas la sienne.
 */
export function toFeatureDisabledError(error: unknown, fallbackFeature?: string): FeatureDisabledError | null {
  if (error instanceof FeatureDisabledError) return error;
  const body = forbiddenBodyOf(error);
  if (body === null || body.code !== FEATURE_DISABLED_CODE) return null;
  const named = typeof body.feature === 'string' && body.feature.trim() !== '' ? body.feature.trim() : null;
  return new FeatureDisabledError(named ?? fallbackFeature ?? UNKNOWN_FEATURE);
}

/** Vrai si `error` est un 403 `feature.disabled` (de `feature` si précisée). */
export function isFeatureDisabledError(error: unknown, feature?: string): boolean {
  const translated = toFeatureDisabledError(error, feature);
  return translated !== null && (feature === undefined || translated.feature === feature);
}
